import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle2, ChevronRight, Lock, Sparkles,
  RefreshCw, ShoppingCart, Download, Calendar,
  Flame, ChefHat, ArrowRight, BadgeCheck, Tag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SEOMeta } from '@/components/SEOMeta';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useAppSettings } from '@/hooks/useAppSettings';

const PRICING_BREADCRUMBS = [{ label: 'Цени', href: '/pricing' }];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.38, delay },
});

const FREE_FEATURES = [
  'TDEE калкулатор — изчисли нуждите си',
  'Ден 1 от персонализирания план (безплатно)',
  'Закуска, обяд, вечеря и снак за понеделник',
  'Пълни рецепти и съставки за Ден 1',
  'Макронутриенти за всяко хранене',
];

const PRO_FEATURES = [
  { label: 'Пълен 7-дневен персонализиран план', detail: 'Ден 1 до Ден 7 — нов план всяка седмица' },
  { label: 'Автоматичен списък за пазаруване', detail: '3 бюджетни варианта — Нисък, Стандарт, Премиум' },
  { label: 'Пълни рецепти с начин на приготвяне', detail: 'Стъпка по стъпка за всяко хранене' },
  { label: 'Точни макроси и калории', detail: 'Белтъци, въглехидрати, мазнини за всяко ястие' },
  { label: 'Изтегли плана като PDF', detail: 'Принтирай или запази за офлайн ползване' },
  { label: 'Неограничени нови планове', detail: 'Генерирай нов план по всяко време' },
  { label: 'Филтър за изключени храни', detail: 'Алергии, непоносимости, предпочитания' },
  { label: 'Отмени по всяко време', detail: 'Без договор, без скрити такси' },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    icon: Flame,
    title: 'Попълни калкулатора',
    desc: 'Въведи възраст, тегло, височина, цел и хранителни предпочитания. Отнема под 3 минути.',
  },
  {
    step: '02',
    icon: Calendar,
    title: 'Получи Ден 1 безплатно',
    desc: 'Виж пълния хранителен план за понеделник — рецепти, съставки и макроси. Без карта.',
  },
  {
    step: '03',
    icon: Sparkles,
    title: 'Абонирай се за пълен достъп',
    desc: 'Отключи всичките 7 дни, списъка за пазар и PDF изтеглянето — на специална цена.',
  },
];

const FAQS = [
  {
    q: 'Какво получавам с абонамента?',
    a: 'Достъп до пълния 7-дневен хранителен план (не само Ден 1), автоматичен списък за пазаруване в 3 бюджетни варианта, PDF изтегляне и неограничено генериране на нови планове.',
  },
  {
    q: 'Мога ли да отменя абонамента?',
    a: 'Да, по всяко време. Без минимален срок и без неустойки. Достъпът остава активен до края на платения период.',
  },
  {
    q: 'Колко често мога да генерирам нов план?',
    a: 'Неограничено. Като абонат можеш да генерираш нов персонализиран план по всяко време — при промяна на цел, тегло или предпочитания.',
  },
  {
    q: 'Планът съобразен ли е с моята диета?',
    a: 'Да. Поддържаме традиционна и веганска диета, а можеш да изключиш конкретни храни (алергии, непоносимости, нехаресвани продукти).',
  },
  {
    q: 'Как се изчисляват калориите?',
    a: 'Използваме Mifflin-St Jeor за TDEE и Atwater 4-4-9 система за точно изчисляване на макронутриенти. Всяко ястие е математически балансирано.',
  },
];

export default function PricingPage() {
  const [openFaq, setOpenFaq] = React.useState(null);
  const { user } = useAuth();
  const isSubscribed = user?.subscription_status === 'active';
  const { subscription_price_eur: price } = useAppSettings();
  const priceStr = `€${Number(price).toFixed(2)}`;

  return (
    <>
      <SEOMeta
        title="Цени — Абонамент за пълен достъп"
        description={`Ден 1 безплатно. За ${priceStr}/месец получи пълен 7-дневен персонализиран хранителен план, списък за пазаруване и PDF изтегляне.`}
        keywords={`portion.bg цени, абонамент хранителен план, ${priceStr} месечен план, кредити portion цена`}
        canonical="/pricing"
        breadcrumbs={PRICING_BREADCRUMBS}
        schemas={[
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'portion.bg Premium',
            applicationCategory: 'HealthApplication',
            operatingSystem: 'Web Browser',
            url: 'https://portion.bg',
            description: 'Пълен 7-дневен AI хранителен план с неограничено генериране, PDF изтегляне, списък за пазаруване и проследяване на напредъка.',
            offers: [
              {
                '@type': 'Offer',
                name: 'Безплатен план',
                price: '0',
                priceCurrency: 'EUR',
                availability: 'https://schema.org/InStock',
                description: 'TDEE калкулатор и Ден 1 от персонализирания план. Без кредитна карта.',
              },
              {
                '@type': 'Offer',
                name: 'Премиум абонамент',
                price: price || '7.99',
                priceCurrency: 'EUR',
                availability: 'https://schema.org/InStock',
                description: 'Пълен 7-дневен план, неограничено генериране, PDF изтегляне, списък за пазаруване.',
                priceSpecification: {
                  '@type': 'UnitPriceSpecification',
                  price: price || '7.99',
                  priceCurrency: 'EUR',
                  unitCode: 'MON',
                  billingDuration: 1,
                  billingIncrement: 1,
                },
              },
            ],
            author: { '@id': 'https://portion.bg/#organization' },
          },
        ]}
      />
      <PageBreadcrumb items={PRICING_BREADCRUMBS} />
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14">

      {/* ── Hero ── */}
      <motion.div className="text-center mb-14" {...fadeUp()}>
        <Badge className="bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-600 border mb-4 font-medium">Ценообразуване</Badge>
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4 leading-tight">
          Един план.<br className="hidden sm:block" /> Едно решение.
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-lg max-w-lg mx-auto">
        Ден 1 е винаги безплатен. За пълния 7-дневен план — {priceStr}/месец. Без изненади.
        </p>
      </motion.div>

      {/* ── Pricing Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-16 items-start" data-testid="pricing-section">

        {/* Free */}
        <motion.div {...fadeUp(0.05)}>
          <Card className="rounded-2xl border border-gray-200 dark:border-slate-700" style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)' }}>
            <CardContent className="p-7 flex flex-col">
              <div className="mb-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">Безплатен</p>
                <div className="flex items-end gap-1 mb-1">
                  <span className="text-5xl font-bold text-gray-900 dark:text-gray-100">€0</span>
                </div>
                <p className="text-sm text-gray-400 dark:text-gray-500">Завинаги безплатно</p>
              </div>

              <ul className="space-y-3 mb-8">
                {FREE_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
                <li className="flex items-start gap-2.5 text-sm text-gray-300 dark:text-gray-600 mt-1">
                  <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Ден 2–7 заключени
                </li>
                <li className="flex items-start gap-2.5 text-sm text-gray-300 dark:text-gray-600">
                  <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Списък за пазаруване заключен
                </li>
              </ul>

              <Link to="/calculator" data-testid="pricing-free-cta">
                <Button variant="outline" className="w-full min-h-11 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-slate-500 hover:text-gray-900 dark:hover:text-gray-100 rounded-xl dark:bg-transparent">
                  Изчисли TDEE безплатно <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>

        {/* Pro Subscription */}
        <motion.div {...fadeUp(0.10)}>
          {/* overflow-hidden removed — use rounded-t-2xl on the accent header instead */}
          <Card className="rounded-2xl border-0 relative" style={{ background: '#0f0f0f', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            {/* Top accent — rounded-t-2xl to match card corners */}
            <div className="bg-emerald-500 text-white text-xs font-bold uppercase tracking-widest text-center py-2 flex items-center justify-center gap-1.5 rounded-t-2xl">
              <Sparkles className="w-3 h-3" /> Пълен достъп
            </div>
            <CardContent className="p-7 flex flex-col">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">Месечен абонамент</p>
                  {isSubscribed && (
                    <span className="flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                      <BadgeCheck className="w-3.5 h-3.5" /> Активен
                    </span>
                  )}
                </div>
                <div className="flex items-end gap-2 mb-1">
                  <span className="text-5xl font-bold text-white">{priceStr}</span>
                  <span className="text-gray-400 text-sm mb-2">/месец</span>
                </div>
                <p className="text-sm text-gray-500">Отмени по всяко време • Без договор</p>
              </div>

              <ul className="space-y-3 mb-8">
                {PRO_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">{f.label}</p>
                      <p className="text-xs text-gray-500 leading-snug">{f.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>

              {isSubscribed ? (
                <>
                  <div className="flex items-center gap-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3 mb-3">
                    <BadgeCheck className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-emerald-300">Вече си абониран</p>
                      <p className="text-xs text-emerald-500/70">Имаш пълен достъп до всички функции</p>
                    </div>
                  </div>
                  <Link to="/my-plan" data-testid="pricing-pro-manage">
                    <Button className="w-full min-h-11 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-xl text-sm gap-2 border border-white/10">
                      Виж моя план <ChevronRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <Tag className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    <p className="text-xs text-gray-500">Имаш промо код? Въведи го при плащане</p>
                  </div>
                  <Link to="/checkout" data-testid="pricing-pro-cta">
                    <Button className="w-full min-h-11 bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-xl text-base gap-2">
                      <Sparkles className="w-4 h-4" />
                      Абонирай се за {priceStr}/месец
                    </Button>
                  </Link>
                  <div className="flex items-center justify-center gap-1.5 mt-3 pb-1">
                    <Lock className="w-3 h-3 text-gray-400" />
                    <p className="text-xs text-gray-400 font-medium">Сигурно плащане чрез Stripe</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* ── How it works ── */}
      <motion.div className="mb-16" {...fadeUp(0.05)}>
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Как работи?</h2>
          <p className="text-slate-500 dark:text-slate-400">Три стъпки до персонализиран хранителен режим</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {HOW_IT_WORKS.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div key={i} {...fadeUp(i * 0.08)}>
                <div className="relative">
                  {i < HOW_IT_WORKS.length - 1 && (
                    <div className="hidden sm:block absolute top-8 left-[calc(100%-12px)] w-full z-0">
                      <ArrowRight className="w-5 h-5 text-gray-200 mx-auto" />
                    </div>
                  )}
        <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl p-6 relative z-10"
                    style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.05)' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-bold text-gray-300 dark:text-gray-600 tabular-nums">{step.step}</span>
                      <div className="w-9 h-9 bg-gray-900 dark:bg-slate-600 rounded-xl flex items-center justify-center">
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1.5">{step.title}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* ── Feature comparison bar ── */}
      <motion.div className="mb-16" {...fadeUp(0.05)}>
        <div className="bg-gray-900 rounded-2xl p-6 sm:p-8">
          <h2 className="text-lg font-bold text-white mb-6 text-center">Какво включва абонаментът?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: Calendar, label: '7 пълни дни', sub: 'Понеделник до неделя' },
              { icon: ShoppingCart, label: 'Списък за пазар', sub: '3 бюджетни варианта' },
              { icon: ChefHat, label: 'Пълни рецепти', sub: 'Начин на приготвяне' },
              { icon: Download, label: 'PDF изтегляне', sub: 'Принтирай плана' },
              { icon: RefreshCw, label: 'Нов план всяка седмица', sub: 'Неограничено' },
              { icon: Flame, label: 'Точни макроси', sub: 'Б / В / М за всяко ястие' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3">
                  <div className="w-8 h-8 bg-emerald-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{item.label}</p>
                    <p className="text-xs text-gray-500">{item.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* ── FAQ ── */}
      <motion.div className="mb-12" {...fadeUp(0.05)}>
        <div className="bg-gray-900 rounded-3xl px-6 sm:px-10 py-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Често задавани въпроси</h2>
            <p className="text-gray-500 text-sm">Всичко, което трябва да знаеш преди да се абонираш</p>
          </div>
          <div className="space-y-2 max-w-2xl mx-auto">
            {FAQS.map((faq, i) => (
              <motion.div key={i} {...fadeUp(i * 0.04)}>
                <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full text-left px-5 py-4 flex items-center gap-4 transition-colors hover:bg-white/5"
                    data-testid={`faq-item-${i}`}
                  >
                    <span className="text-xs font-black text-emerald-500 tabular-nums w-5 flex-shrink-0">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="flex-1 font-medium text-white/90 text-sm text-left">{faq.q}</span>
                    <ChevronRight
                      className="w-4 h-4 text-white/25 flex-shrink-0 transition-transform duration-250"
                      style={{ transform: openFaq === i ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    />
                  </button>

                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: 'easeOut' }}
                      style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(5,150,105,0.06)' }}
                      className="px-5 py-4"
                    >
                      <p className="text-sm text-white/55 leading-relaxed pl-9">{faq.a}</p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ── Bottom CTA ── */}
      <motion.div className="text-center" {...fadeUp(0.05)}>
        <div className="bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl py-10 px-6"
          style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.03)' }}>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Готов да започнеш?</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
            Изчисли калориите си безплатно и виж Ден 1 от персонализирания ти план — без карта.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/calculator">
              <Button variant="outline" className="min-h-11 px-6 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-slate-500 rounded-xl dark:bg-transparent"
                data-testid="pricing-bottom-free-cta">
                Изчисли TDEE безплатно
              </Button>
            </Link>
            <Link to="/my-plan">
              <Button className="min-h-11 px-6 bg-gray-900 dark:bg-slate-100 hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-gray-900 rounded-xl gap-2"
                data-testid="pricing-bottom-pro-cta">
                <Sparkles className="w-4 h-4" />
                {isSubscribed ? 'Виж моя план' : `Абонирай се за ${priceStr}/месец`}
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>

    </div>
    </>
  );
}
